/**
 * Module: web/src/composables/useApi.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.2.0
 * Date: 2026-07-14
 *
 * v1.2.0 (error observability, OPERATIONS-S10 -- contract
 * error-observability-api v1.0.0 SS4.2): every thrown ApiError also reports
 * to the client error beacon -- status 0 as 'request_failed' (the
 * zero-server-trace class), status > 0 as 'api_error' (status/method/path/
 * code ride detail). Exclusions mirror the contract: /auth/* 401s are
 * flow-expected (invalid login, the router guard's anonymous whoami probe on
 * every page load) and never report. The beacon module imports nothing from
 * here (no cycle); its delivery uses raw fetch, so its own failures can
 * never re-enter this hook.
 *
 * v1.1.0 (error-layer honesty, OPERATIONS-S9 -- the Ashley mark-paid
 * incident): a rejected fetch() (network down, DNS/TLS, or an edge/CDN
 * security challenge answering before the origin) now rethrows as
 * ApiError(status=0, code='network') instead of a bare TypeError, so every
 * caller's catch can distinguish "the server said no" (real status/code)
 * from "the request never reached the portal" (status 0) -- the latter
 * leaves ZERO server-side trace and previously rendered the same generic
 * message as a server rejection.
 *
 * Central fetch wrapper for /api/* -- the single request layer (doc 04 sect 2.2 + 10).
 *
 *   - cookie session: credentials:'include' on every request
 *   - CSRF: the required X-Requested-With: XMLHttpRequest header on EVERY
 *     non-GET (server 403s {code:'csrf'} without it -- decision D-10)
 *   - errors normalize to a thrown ApiError carrying {status, code, detail, body}
 *   - central auth failure handling:
 *       401 on any non-/auth/ endpoint  -> session died -> redirect to /login
 *       403 {code:'must_change_password'} -> forced-change wall -> /change-password
 *     (401s on /auth/* endpoints are flow-expected -- invalid login, unauthenticated
 *      whoami -- and are left to the caller.)
 *
 * The redirect itself is delegated to a handler registered by the router module
 * (setAuthFailureHandler) so this file has zero import cycle with the router.
 */

import { reportApiFailure } from '@/lib/errorBeacon';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

export type AuthFailureKind = 'unauthenticated' | 'must_change_password';
type AuthFailureHandler = (kind: AuthFailureKind) => void;

let authFailureHandler: AuthFailureHandler | null = null;

/** Registered once by the router module; invoked on central 401/forced-change 403. */
export function setAuthFailureHandler(handler: AuthFailureHandler): void {
  authFailureHandler = handler;
}

/** Normalized API error. `code` is the contract's machine code ('invalid', 'locked', 'csrf', 'policy', ...). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly detail: string | null;
  /** Parsed JSON error body (e.g. carries unlock_at on 423 {code:'locked'}). */
  readonly body: unknown;

  constructor(status: number, code: string | null, detail: string | null, body: unknown, path: string) {
    super(`API ${status}${code ? ` [${code}]` : ''}: ${detail ?? 'request failed'} (${path})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.body = body;
  }

  /** Convenience accessor for extra fields on the error body (e.g. unlock_at). */
  field(name: string): unknown {
    if (this.body && typeof this.body === 'object' && name in (this.body as Record<string, unknown>)) {
      return (this.body as Record<string, unknown>)[name];
    }
    return undefined;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') {
    // CSRF: custom required header on every state-changing request (D-10).
    headers['X-Requested-With'] = 'XMLHttpRequest';
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include', // cookie session (__Host-ops_sid)
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // fetch() itself rejected: the request NEVER reached the portal --
    // connection loss, DNS/TLS, or an edge security layer swallowing it.
    // status 0 is the caller's signal to say so instead of blaming the server.
    const err = new ApiError(0, 'network',
      'request never reached the portal (connection or security layer)',
      null, path);
    // Beacon: the class NO server-side log can ever hold (contract SS3).
    reportApiFailure({ status: 0, method, path, code: 'network',
                       message: err.message });
    throw err;
  }

  // Parse body once (may legitimately be empty on 204/202).
  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null; // non-JSON body -- treat as empty
    }
  }

  if (!res.ok) {
    const obj = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    const code = typeof obj.code === 'string' ? obj.code : null;
    const detail = typeof obj.detail === 'string' ? obj.detail : null;
    const err = new ApiError(res.status, code, detail, parsed, path);

    const isAuthEndpoint = path.startsWith('/auth/');
    // Beacon (contract SS4.2 exclusions): /auth/* 401s are flow-expected
    // (invalid login, anonymous whoami probe) -- everything else reports,
    // INCLUDING non-auth 401s (session died mid-flight, the Ashley 2h-idle
    // datum) so the server row gains its client-context sibling.
    if (!(res.status === 401 && isAuthEndpoint)) {
      reportApiFailure({ status: res.status, method, path, code,
                         message: err.message });
    }
    if (res.status === 401 && !isAuthEndpoint) {
      // Session expired / revoked mid-flight -> central redirect to login.
      authFailureHandler?.('unauthenticated');
    } else if (res.status === 403 && code === 'must_change_password') {
      // Forced-change wall (doc 04 sect 6.3) -> central redirect to /change-password.
      authFailureHandler?.('must_change_password');
    }
    throw err;
  }

  return parsed as T;
}

export function apiGet<T>(path: string, query?: string): Promise<T> {
  return request<T>('GET', query ? `${path}?${query}` : path);
}

export function apiPost<T, B = unknown>(path: string, body?: B): Promise<T> {
  return request<T>('POST', path, body);
}

export function apiPut<T, B = unknown>(path: string, body?: B): Promise<T> {
  return request<T>('PUT', path, body);
}

export function apiPatch<T, B = unknown>(path: string, body?: B): Promise<T> {
  return request<T>('PATCH', path, body);
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

/**
 * Tolerant list unwrap: the contract pins {entries:[...]} for /my-entries but
 * leaves other list envelopes unspecified -- accept either a bare array or a
 * {<key>: [...]} envelope (first matching key wins) so the SPA is resilient
 * to either reading of the contract.
 */
export function unwrapList<T>(body: unknown, keys: string | string[]): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    for (const key of Array.isArray(keys) ? keys : [keys]) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }
  }
  return [];
}
